/**
 * Access Denied Page
 * Shown when user navigates to a URL they don't have permission to access
 */

import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/context/PermissionsContext';
import { Button } from '@/components/ui/button';
import { ShieldX, ArrowLeft, Home, HelpCircle } from 'lucide-react';

export const AccessDenied = () => {
  const navigate = useNavigate();
  const { organization } = useAuth();
  const { role, getRoleDisplayName, navigation } = usePermissions();

  // Get the first available navigation item for the user
  const getDefaultPath = () => {
    if (navigation?.sections?.[0]?.items?.[0]?.path) {
      return navigation.sections[0].items[0].path;
    }
    return '/app';
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="w-20 h-20 rounded-full bg-coral-red/10 flex items-center justify-center mx-auto mb-6">
          <ShieldX className="w-10 h-10 text-coral-red" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-cloud-white mb-2">
          Access Denied
        </h1>

        {/* Description */}
        <p className="text-soft-silver mb-6">
          You don't have permission to access this page.
          {role && (
            <span className="block mt-2 text-sm text-muted-text">
              Your current role is <span className="text-electric-ice-blue">{getRoleDisplayName()}</span>
              {organization && ` in ${organization.name}`}.
            </span>
          )}
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="border-white/[0.08] text-cloud-white hover:bg-white/[0.05]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
          
          <Button
            onClick={() => navigate(getDefaultPath())}
            className="bg-electric-ice-blue text-obsidian hover:bg-electric-ice-blue/90"
          >
            <Home className="w-4 h-4 mr-2" />
            Go to Dashboard
          </Button>
        </div>

        {/* Help Link */}
        <div className="mt-8 pt-6 border-t border-white/[0.08]">
          <p className="text-sm text-muted-text mb-3">
            Think you should have access?
          </p>
          <Link
            to="/app/help"
            className="inline-flex items-center gap-2 text-sm text-electric-ice-blue hover:underline"
          >
            <HelpCircle className="w-4 h-4" />
            Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AccessDenied;
